import type { ReactElement, ReactNode } from "react";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { chromeSurface, color, radius, space } from "@tokens/index";
import { Badge, IconButton, Text } from "../../primitives";
import type { Workflow } from "../../../experience-shell/workflows";
import { sceneMeasures } from "./measures";
import styles from "./SceneHeader.module.css";

export interface SceneHeaderProps {
  readonly workflow: Pick<
    Workflow,
    "title" | "kind" | "entityType" | "entityId" | "entityDisplayName" | "parentWorkflowId"
  >;
  /** Leave the workflow scene. Hidden when a compact panel shows its own back. */
  readonly onBack: (() => void) | undefined;
  readonly parentTitle: string | undefined;
  readonly onNavigateToParent: (() => void) | undefined;
  /** Open Einstein's workflow list for the entity. */
  readonly onOpenEntity: (() => void) | undefined;
  /** Open the entity in Galileo. */
  readonly onNavigateToGalileoEntity: (() => void) | undefined;
  /** Live viewers of the workflow. */
  readonly presence: ReactNode;
  /** Board / Timeline control. */
  readonly viewSwitcher: ReactNode;
  /** Queue triage. Trailing, after the view switcher. */
  readonly next?: ReactNode;
  /** Below `medium` the meta chips drop so the title keeps its room. */
  readonly compact: boolean;
}

/**
 * One-row scene header, 48px, sticky. Back · entity chip · title · parent
 * chip · kind · presence · view switcher. The header is stable across the
 * Board/Timeline switch: the scene renders it once, outside the crossfade.
 * The row is chrome, one tonal step above the canvas; no outer frame.
 */
export function SceneHeader({
  workflow,
  onBack,
  parentTitle,
  onNavigateToParent,
  onOpenEntity,
  onNavigateToGalileoEntity,
  presence,
  viewSwitcher,
  next,
  compact,
}: SceneHeaderProps): ReactElement {
  const showParent = workflow.parentWorkflowId !== null && parentTitle !== undefined;
  return (
    <header
      data-testid="scene-header"
      style={{
        display: "flex",
        alignItems: "center",
        gap: space[3],
        height: sceneMeasures.headerHeight,
        padding: `0 ${space[4]}`,
        flexShrink: 0,
        minWidth: 0,
        position: "sticky",
        top: 0,
        zIndex: 5,
        ...chromeSurface,
      }}
    >
      {onBack !== undefined && (
        <IconButton
          icon={ArrowLeft}
          variant="ghost"
          size="md"
          aria-label="Back"
          tooltip="Back"
          onClick={onBack}
        />
      )}

      {!compact && (
        <EntityChip
          workflow={workflow}
          onOpenEntity={onOpenEntity}
          onNavigateToGalileoEntity={onNavigateToGalileoEntity}
        />
      )}

      <div style={{ display: "flex", alignItems: "center", gap: space[2], flex: 1, minWidth: 0 }}>
        <Text
          as="h1"
          role="title"
          size="md"
          weight="semibold"
          truncate
          style={{ margin: 0, minWidth: 0 }}
        >
          {workflow.title}
        </Text>
        {showParent && (
          <ParentWorkflowChip parentTitle={parentTitle} onNavigateToParent={onNavigateToParent} />
        )}
        {!compact && workflow.kind.length > 0 && (
          <Text role="caption" size="xs" tone="subtle" truncate style={{ flexShrink: 0 }}>
            {workflow.kind}
          </Text>
        )}
      </div>

      {presence !== null && presence !== undefined && (
        <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>{presence}</div>
      )}
      {viewSwitcher}
      {next}
    </header>
  );
}

/** "from: <parent>" link. Reads as a chip, sits inline after the title. */
export function ParentWorkflowChip({
  parentTitle,
  onNavigateToParent,
}: {
  readonly parentTitle: string;
  readonly onNavigateToParent: (() => void) | undefined;
}): ReactElement {
  return (
    <button
      type="button"
      className={styles.chip}
      onClick={onNavigateToParent}
      disabled={onNavigateToParent === undefined}
      aria-label={`Open parent workflow: ${parentTitle}`}
      style={{
        appearance: "none",
        display: "inline-flex",
        alignItems: "center",
        gap: space[1],
        height: 24,
        padding: `0 ${space[2]}`,
        border: "none",
        borderRadius: radius.pill,
        color: color.accent.primary,
        cursor: onNavigateToParent === undefined ? "default" : "pointer",
        flexShrink: 1,
        minWidth: 0,
        maxWidth: 240,
      }}
    >
      <ArrowLeft size={11} strokeWidth={2.25} aria-hidden="true" />
      <Text role="caption" size="xs" truncate style={{ color: "inherit" }}>
        from: {parentTitle}
      </Text>
    </button>
  );
}

/**
 * Entity type badge and id, as one chip. The chip is the link to the
 * entity's workflow list; the trailing icon opens Galileo.
 */
function EntityChip({
  workflow,
  onOpenEntity,
  onNavigateToGalileoEntity,
}: {
  readonly workflow: SceneHeaderProps["workflow"];
  readonly onOpenEntity: (() => void) | undefined;
  readonly onNavigateToGalileoEntity: (() => void) | undefined;
}): ReactElement {
  const label =
    workflow.entityDisplayName.length > 0 ? workflow.entityDisplayName : workflow.entityId;
  const inner = (
    <>
      <Badge variant="neutral" size="sm" style={{ flexShrink: 0 }}>
        {workflow.entityType}
      </Badge>
      <Text role="caption" size="xs" tone="muted" truncate style={{ color: "inherit" }}>
        {label}
      </Text>
    </>
  );
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 2,
        flexShrink: 0,
        maxWidth: 280,
        minWidth: 0,
      }}
    >
      {onOpenEntity !== undefined ? (
        <button
          type="button"
          className={styles.chip}
          data-hit-area=""
          onClick={onOpenEntity}
          aria-label={`View all workflows for ${label}`}
          style={{
            appearance: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: space[2],
            height: 28,
            padding: `0 ${space[2]} 0 2px`,
            border: "none",
            borderRadius: radius.pill,
            color: color.fg.muted,
            cursor: "pointer",
            minWidth: 0,
          }}
        >
          {inner}
        </button>
      ) : (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: space[2],
            color: color.fg.muted,
            minWidth: 0,
          }}
        >
          {inner}
        </span>
      )}
      {onNavigateToGalileoEntity !== undefined && (
        <IconButton
          icon={ExternalLink}
          size="sm"
          variant="ghost"
          aria-label={`Open ${label} in Galileo`}
          tooltip="Open in Galileo"
          onClick={onNavigateToGalileoEntity}
        />
      )}
    </div>
  );
}
